import { useEffect, useRef, useState } from "react";
import { Q } from "@nozbe/watermelondb";
import { Alert, Button, Chip, OutlinedInput, Typography } from "@mui/material";
import { Send } from "@mui/icons-material";
import Handlebars from "handlebars";
import { database } from "..";
import SmsMessage from "../model/smsMessage";
import { APISms, SmsStatus, getSmsStatus, sendSms } from "../api/sms";
import { APICustomer } from "../api/customers";
import { getBusinessAddress } from "../api/settings";
import threadStyle from "./SmsThread.module.css";

// Quick texts; {{firstName}}, {{business}} etc. are filled in.
export const QUICK_TEXTS = [
  {
    label: "On my way",
    body: "Hi {{firstName}}, this is {{business}}. We're on our way and should arrive shortly.",
  },
  {
    label: "Estimate ready",
    body: "Hi {{firstName}}, your estimate from {{business}} is ready. Reply here with any questions or to schedule the work.",
  },
  {
    label: "Appointment reminder",
    body: "Hi {{firstName}}, a reminder from {{business}} that we'll be at {{address}} for your appointment. Reply if you need to change the time.",
  },
  {
    label: "Thank you",
    body: "Thanks for choosing {{business}}, {{firstName}}! If you were happy with the work, we'd really appreciate a review.",
  },
];

export function fillTemplate(
  body: string,
  customer: APICustomer,
  business: string,
  extra: Record<string, string> = {}
) {
  try {
    return Handlebars.compile(body, { noEscape: true })({
      firstName: customer.firstName,
      lastName: customer.lastName,
      address: customer.addressLine1,
      business,
      ...extra,
    });
  } catch {
    return body;
  }
}

function statusText(m: APISms) {
  switch (m.status) {
    case "queued":
      return "Sending…";
    case "sent":
      return "Sent";
    case "failed":
      return `Not sent: ${m.error || "unknown error"}`;
    default:
      return "";
  }
}

function SmsThread({ customer }: { customer: APICustomer }) {
  const [messages, setMessages] = useState<APISms[]>([]);
  const [status, setStatus] = useState<SmsStatus>();
  const [draft, setDraft] = useState("");
  const [business, setBusiness] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getSmsStatus().then(setStatus);
    getBusinessAddress().then((a) => setBusiness(a.name || "Lifetree"));
  }, []);

  // Live: new replies and status changes appear as soon as they sync.
  useEffect(() => {
    if (!customer.id) return;
    const sub = database
      .get<SmsMessage>("sms_messages")
      .query(Q.where("customer_id", customer.id))
      .observeWithColumns(["status", "error"])
      .subscribe((records) =>
        setMessages(
          records
            .map((m) => ({
              id: m.id,
              customerId: customer.id!,
              direction: m.direction === "in" ? "in" : "out",
              body: m.body || "",
              toNumber: m.toNumber || "",
              fromNumber: m.fromNumber || "",
              status: (m.status as APISms["status"]) || "queued",
              error: m.error || "",
              createdAt: Number(m.createdAt) || 0,
            }))
            .sort((a, b) => a.createdAt - b.createdAt) as APISms[]
        )
      );
    return () => sub.unsubscribe();
  }, [customer.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages.length]);

  const hasPhone = customer.phone.replace(/\D/g, "").length >= 10;
  const canSend = !!status?.enabled && hasPhone && draft.trim() !== "";

  const send = async () => {
    if (!canSend) return;
    await sendSms(customer.id!, customer.phone, draft.trim());
    setDraft("");
  };

  return (
    <div className={threadStyle.thread}>
      {status && !status.enabled && (
        <Alert severity="info">
          Texting from the CRM isn't set up yet (Settings shows how).{" "}
          {hasPhone && (
            <a href={`sms:${customer.phone.replace(/[^\d+]/g, "")}`}>
              Text from this phone instead
            </a>
          )}
        </Alert>
      )}
      {!hasPhone && (
        <Typography variant="body2" color="text.secondary">
          Add a phone number to text this customer.
        </Typography>
      )}

      {messages.length > 0 && (
        <div className={threadStyle.messages}>
          {messages.map((m) => (
            <div
              key={m.id}
              className={
                m.direction === "out" ? threadStyle.outRow : threadStyle.inRow
              }
            >
              <div
                className={
                  m.direction === "out" ? threadStyle.out : threadStyle.in
                }
              >
                {m.body}
              </div>
              <div
                className={
                  m.status === "failed" ? threadStyle.metaError : threadStyle.meta
                }
              >
                {new Date(m.createdAt).toLocaleString([], {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
                {m.direction === "out" && ` · ${statusText(m)}`}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}

      {status?.enabled && hasPhone && (
        <>
          <div className={threadStyle.quick}>
            {QUICK_TEXTS.map((q) => (
              <Chip
                key={q.label}
                label={q.label}
                variant="outlined"
                size="small"
                onClick={() => setDraft(fillTemplate(q.body, customer, business))}
              />
            ))}
          </div>
          <div className={threadStyle.composer}>
            <OutlinedInput
              multiline
              maxRows={6}
              fullWidth
              placeholder={`Text ${customer.firstName || "customer"}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              size="small"
            />
            <Button
              variant="contained"
              onClick={send}
              disabled={!canSend}
              aria-label="Send text"
            >
              <Send fontSize="small" />
            </Button>
          </div>
          <Typography variant="caption" color="text.secondary">
            {draft.length > 160
              ? `${draft.length} characters · sends as ${Math.ceil(
                  draft.length / 153
                )} texts`
              : `${draft.length}/160`}
          </Typography>
        </>
      )}
    </div>
  );
}

export default SmsThread;
